import { Check, ListFilter, Search, X } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import type { ColumnFilterConfig, ColumnFilterOption } from './types';

interface ColumnFilterProps {
  config: ColumnFilterConfig;
  /** The bare column header label, used as the popover heading. */
  columnLabel: string;
}

function isActive(config: ColumnFilterConfig): boolean {
  switch (config.kind) {
    case 'select':
      return config.value !== null && config.value !== '';
    case 'multiselect':
      return config.value.length > 0;
    case 'text':
      return config.value.trim() !== '';
  }
}

/**
 * Funnel button rendered inside a column header. Click opens a refined
 * popover with the per-column filter UI. Active state stays calm — a small
 * mint dot in the corner of the funnel signals "this column is filtered"
 * without flooding the header with a saturated pill.
 */
export function ColumnFilter({ config, columnLabel }: ColumnFilterProps) {
  const [open, setOpen] = useState(false);
  const active = isActive(config);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter ${columnLabel}`}
          aria-pressed={active}
          onClick={(e) => {
            // Header buttons are wrapped in a sortable button — keep the funnel
            // click out of the sort handler.
            e.stopPropagation();
          }}
          className={cn(
            'relative inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors duration-150',
            'focus-visible:shadow-(--shadow-focus-ring-solid) focus-visible:outline-none',
            active
              ? 'text-primary-700 hover:bg-primary-50/80'
              : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700',
            'data-[state=open]:bg-neutral-100 data-[state=open]:text-neutral-700',
          )}
        >
          <ListFilter className="h-3.5 w-3.5" strokeWidth={active ? 2 : 1.75} aria-hidden="true" />
          {active ? (
            <span
              aria-hidden="true"
              className="bg-primary-500 ring-surface absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full ring-2"
            />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-72 overflow-hidden p-0"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <header className="flex items-center justify-between gap-2 px-3.5 pt-3 pb-2">
          <div className="flex items-center gap-1.5">
            <ListFilter
              className="text-primary-600 h-3.5 w-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <span className="text-meta-sm tracking-eyebrow font-semibold text-neutral-700 uppercase">
              {columnLabel}
            </span>
          </div>
          {active ? (
            <button
              type="button"
              onClick={() => {
                clearFilter(config);
                setOpen(false);
              }}
              className={cn(
                'text-meta-sm rounded-sm px-1.5 py-0.5 text-neutral-500 transition-colors',
                'hover:bg-neutral-100 hover:text-neutral-900',
                'focus-visible:shadow-(--shadow-focus-ring-solid) focus-visible:outline-none',
              )}
            >
              Clear
            </button>
          ) : null}
        </header>
        <div className="border-t border-(--color-border)">
          <FilterBody
            config={config}
            onApplied={() => {
              if (config.kind === 'select') setOpen(false);
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function clearFilter(config: ColumnFilterConfig) {
  switch (config.kind) {
    case 'select':
      config.onChange(null);
      return;
    case 'multiselect':
      config.onChange([]);
      return;
    case 'text':
      config.onChange('');
      return;
  }
}

function FilterBody({ config, onApplied }: { config: ColumnFilterConfig; onApplied: () => void }) {
  if (config.kind === 'select') {
    return <SelectBody config={config} onApplied={onApplied} />;
  }
  if (config.kind === 'multiselect') {
    return <MultiselectBody config={config} />;
  }
  return <TextBody config={config} />;
}

function SelectBody({
  config,
  onApplied,
}: {
  config: Extract<ColumnFilterConfig, { kind: 'select' }>;
  onApplied: () => void;
}) {
  const { options, value, onChange } = config;
  return (
    <div className="max-h-72 overflow-y-auto py-1">
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <FilterOptionRow
            key={opt.value}
            option={opt}
            selected={selected}
            onClick={() => {
              onChange(selected ? null : opt.value);
              onApplied();
            }}
          />
        );
      })}
    </div>
  );
}

function MultiselectBody({
  config,
}: {
  config: Extract<ColumnFilterConfig, { kind: 'multiselect' }>;
}) {
  const { options, value, onChange } = config;
  const selected = useMemo(() => new Set(value), [value]);
  return (
    <div className="max-h-72 overflow-y-auto py-1">
      {options.map((opt) => {
        const isOn = selected.has(opt.value);
        return (
          <FilterOptionRow
            key={opt.value}
            option={opt}
            selected={isOn}
            onClick={() => {
              const next = isOn ? value.filter((v) => v !== opt.value) : [...value, opt.value];
              onChange(next);
            }}
          />
        );
      })}
    </div>
  );
}

function FilterOptionRow({
  option,
  selected,
  onClick,
}: {
  option: ColumnFilterOption;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'text-body-sm relative flex w-full items-center justify-between gap-3 px-3.5 py-2 text-left transition-colors duration-100',
        selected
          ? 'bg-primary-50/60 text-primary-800 font-medium'
          : 'text-neutral-700 hover:bg-neutral-50',
        'focus-visible:bg-neutral-50 focus-visible:outline-none',
      )}
    >
      {selected ? (
        <span
          aria-hidden="true"
          className="bg-primary-500 absolute top-1.5 bottom-1.5 left-0 w-0.5 rounded-r"
        />
      ) : null}
      <span className="truncate">{option.label}</span>
      {selected ? (
        <Check className="text-primary-600 h-4 w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
      ) : null}
    </button>
  );
}

function TextBody({ config }: { config: Extract<ColumnFilterConfig, { kind: 'text' }> }) {
  const { value, onChange, placeholder } = config;
  const id = useId();
  return (
    <div className="p-3">
      <label
        htmlFor={id}
        className="relative flex h-9 items-center gap-2 rounded-md border border-(--color-border) bg-neutral-50/60 px-2.5"
      >
        <Search className="h-3.5 w-3.5 shrink-0 text-neutral-400" strokeWidth={1.75} />
        <input
          id={id}
          type="text"
          value={value}
          placeholder={placeholder ?? 'Filter…'}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          className={cn(
            'text-body-sm flex-1 bg-transparent placeholder:text-neutral-400',
            'focus:outline-none',
          )}
        />
        {value ? (
          <button
            type="button"
            onClick={() => {
              onChange('');
            }}
            aria-label="Clear filter"
            className="text-neutral-400 transition-colors hover:text-neutral-700"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.75} />
          </button>
        ) : null}
      </label>
    </div>
  );
}
